import { Observable, of } from 'rxjs';

import {
  AuthService,
  Container,
  ModalService,
  ProgrammingLanguage,
  Service,
  TrackingEventNames,
  TrackingService,
  history,
} from '../../../../symphony';

import {
  ActionType,
  MaterialCTAClickType,
  MaterialQuitAction,
  RequestType,
  TrackActionName,
} from '../../global-interface';
import { wrapByTag } from '../../utils/helpers';
import { SlDataService } from '../sl-data.service';
import { setIsDark } from '../../utils/storageManager';
import { SlActions } from '../ActionsService/sl-actions';
import { SlTrackingService } from './sl-tracking.service';
import { LANGUAGE_TYPES } from '../../constants/LanguageTypes';
import { CodeLanguages } from '../../../../api/private/global.interface';
import {
  ICodeSolution, IMaterialInfo, IMaterialStatus, ITIYMaterialData, MaterialRequirementType, PlaygroundOutputType,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ICodeCompilationResponse, IUserCode } from '../../../../api/public/code-api';
import { CodeLanguagesToProgrammingLanguage, ProgrammingLanguageToCodeLanguages } from '../../../../shared/public/constants/constants';
import { checkIsWeb } from '../../../../shared/public/SlCodeEditorComponents/utils/helperFunctions';
import { SlMaterialService } from './sl-material.service';
import { lePlaygroundContext, tiyCodeInputModalId, tiyCodeResetModalId } from '../../global-constants';
import { SlCodeService } from './sl-code.service';
import { DynamicPaywallLoader } from '../../../../_paywalls/public/DynamicPaywallLoader';
import { Touchpoint } from '../../../../api/public/subscription-api';

@Service()
export class SlEventsService {
  private actions$ = Container.take(lePlaygroundContext.value, SlActions).actions$;

  private dataService = Container.take(lePlaygroundContext.value, SlDataService);

  private materialService = Container.take(lePlaygroundContext.value, SlMaterialService);

  private modalService = Container.take('global', ModalService);

  private codeService = Container.take(lePlaygroundContext.value, SlCodeService);

  private paywallLoader = DynamicPaywallLoader.ensureRegistered();

  private isTryItYourself: boolean = Container.take(
    lePlaygroundContext.value,
    'isTryItYourself',
  );

  private trackingService = Container.take('global', TrackingService);

  private trackAction$ = Container.take(
    lePlaygroundContext.value,
    SlTrackingService,
  ).trackAction$;

  public onPlaygroundContainerMount = (data, refs): Observable<null> => {
    const { topLevelDomain$, routeData$, expandedTIYEditorRefs } = this.dataService;
    if (this.dataService.isWeb$.value) {
      expandedTIYEditorRefs.next(refs);
    } else {
      expandedTIYEditorRefs.next([refs[0]]);
    }

    topLevelDomain$.next(data.topLevelDomain);
    routeData$.next(data.routeData);
    this.actions$.next({
      type: ActionType.InitPlayground,
      requestType: RequestType.Parallel,
      data: {
        topLevelDomain: topLevelDomain$.value,
      },
    });
    return of(null);
  };

  public onTIYExpandClick = (): Observable<null> => {
    const isOwned = this.dataService.materialData$.value?.status.ownership.isOwned;
    const isRequired = this.dataService.materialData$.value?.requirementTypeId !== MaterialRequirementType.Optional;
    if (!isOwned) {
      const touchpoint = isRequired ? Touchpoint.blockedLearning : Touchpoint.tiy;
      this.paywallLoader.openPaywall({
        touchpoint,
        props: {}
      });
      return of(null);
    }
    this.dataService.isTIYExpanded.next(true);
    this.trackingService.tracking$.next({
      eventName: TrackingEventNames.playgroundScreenClick,
      eventParams: {
        clickType: 'MAXIMIZE',
        dynamicId: String(this.dataService.materialData$.value.header.dynamicId || ''),
        materialId: String(this.dataService.materialData$.value.header.materialId || ''),
        staticId: String(this.dataService.materialData$.value.header.staticId || ''),
        version: '2-0-0',
      },
    });
    return of(null);
  };

  public onTIYEditClick = (idx: number) => {
    this.dataService.minimizedTIYEditorRefs.value[idx].current.focusEditorToLastChar();
    this.trackingService.tracking$.next({
      eventName: TrackingEventNames.playgroundScreenClick,
      eventParams: {
        clickType: 'EDIT CODE',
        dynamicId: String(this.dataService.materialData$.value.header.dynamicId || ''),
        materialId: String(this.dataService.materialData$.value.header.materialId || ''),
        staticId: String(this.dataService.materialData$.value.header.staticId || ''),
        version: '2-0-0',
      },
    });
  };

  public onTIYMinimizeClick = (): Observable<null> => {
    this.dataService.isDarkMode$.next(true);
    setIsDark(true);
    this.dataService.isTIYExpanded.next(false);

    if (this.dataService.isWeb$.value) {
      this.dataService.output$.next(null);
    }

    this.trackingService.tracking$.next({
      eventName: TrackingEventNames.playgroundScreenClick,
      eventParams: {
        clickType: 'UNMAXIMIZE',
        dynamicId: String(this.dataService.materialData$.value.header.dynamicId || ''),
        materialId: String(this.dataService.materialData$.value.header.materialId || ''),
        staticId: String(this.dataService.materialData$.value.header.staticId || ''),
        version: '2-0-0',
      },
    });
    return of(null);
  };

  private mutateOutputResponse = (data, isDark?: boolean) => {
    const { isDarkMode$ } = this.dataService;
    let css = '';
    let htmlCode = data?.output;
    const darkMode = isDark || isDarkMode$.value;
    if (
      data?.outputType === PlaygroundOutputType.Web
      && data?.outputStyle
    ) {
      const localDocument = new DOMParser().parseFromString(
        htmlCode,
        'text/html',
      );
      if (localDocument.getElementById('custom-style')) {
        localDocument.getElementById('custom-style').remove();
        htmlCode = localDocument.documentElement.innerHTML;
        htmlCode = wrapByTag(htmlCode, 'html');
      }
      const style = darkMode
        ? data?.outputStyle.dark
        : data?.outputStyle.light;
      css = `<style id="custom-style">${style} </style>`;
      const includeHead = htmlCode ? htmlCode.includes('<head>') : false;
      const htmlTagIndex = htmlCode.indexOf('<html>') + 6;
      if (!includeHead) {
        htmlCode = [
          htmlCode.slice(0, htmlTagIndex),
          `<head>${css}</head>`,
          htmlCode.slice(htmlTagIndex),
        ].join('');
      } else {
        const closeHeadTagIndex = htmlCode.indexOf('</head>');
        htmlCode = [
          htmlCode.slice(0, closeHeadTagIndex),
          css,
          htmlCode.slice(closeHeadTagIndex),
        ].join('');
      }
    }
    return {
      ...data,
      output: htmlCode,
    };
  };

  public onHeaderChangeModeToggle = (state: boolean): Observable<null> => {
    const { output$, isDarkMode$ } = this.dataService;
    isDarkMode$.next(state);
    setIsDark(state);
    output$.next(this.mutateOutputResponse(output$.value, state));
    return of(null);
  };

  public onTIYWidgetRunClick = (): Observable<null> => {
    const isOwned = this.dataService.materialData$.value?.status.ownership.isOwned;
    const isRequired = this.dataService.materialData$.value?.requirementTypeId !== MaterialRequirementType.Optional;
    if (!isOwned) {
      const touchpoint = isRequired ? Touchpoint.blockedLearning : Touchpoint.tiy;
      this.paywallLoader.openPaywall({
        touchpoint,
        props: {}
      });
      return of(null);
    }

    this.codeService.onCompileCode();

    return of(null);
  };

  public onTIYWidgetFocus = (status: IMaterialStatus): Observable<null> => {
    if (!status.ownership.isOwned) {
      const isRequired = this.dataService.materialData$.value?.requirementTypeId !== MaterialRequirementType.Optional;
      const touchpoint = isRequired ? Touchpoint.blockedLearning : Touchpoint.tiy;
      this.paywallLoader.openPaywall({
        touchpoint,
        props: {}
      });
    }
    return of(null);
  };

  public onTIYWidgetMount = (materialInfo: IMaterialInfo, refs) => {
    const codes = (materialInfo.data as ITIYMaterialData).codes;

    this.materialService.handleGetMaterialResponse(materialInfo);
    this.dataService.initialCodes.next(codes);
    this.dataService.isDarkMode$.next(true);
    setIsDark(true);
    if (checkIsWeb(ProgrammingLanguageToCodeLanguages[codes[0].languageId])) {
      this.dataService.isWeb$.next(true);
      this.dataService.minimizedTIYEditorRefs.next(refs);
    } else {
      this.dataService.minimizedTIYEditorRefs.next([refs[0]]);
    }
  };

  public onTIYEditorResetClick = () => {
    this.trackingService.tracking$.next({
      eventName: TrackingEventNames.playgroundScreenClick,
      eventParams: {
        clickType: 'RESET',
        dynamicId: String(this.dataService.materialData$.value.header.dynamicId || ''),
        materialId: String(this.dataService.materialData$.value.header.materialId || ''),
        staticId: String(this.dataService.materialData$.value.header.staticId || ''),
        version: '2-0-0',
      },
    });

    this.modalService.open(tiyCodeResetModalId);
  };

  public onTIYEditorResetConfirm = () => {
    const initialCodes = this.dataService.initialCodes.value;
    this.dataService.minimizedTIYEditorRefs.value?.forEach((e) => {
      e.current?.resetCodes(initialCodes);
    });
    this.dataService.expandedTIYEditorRefs.value?.forEach((e) => {
      e.current?.resetCodes(initialCodes);
    });
    this.setCodes(initialCodes);
    this.dataService.isTIYResetButtonActive.next(false);

    this.dataService.output$.next(null);
    this.dataService.outputConsole$.next(null);

    this.modalService.close(tiyCodeResetModalId);
  };

  private setCodes = (codes: ICodeSolution[]) => {
    if (this.dataService.isWeb$.value) {
      const htmlCode = codes.find((c) => c.languageId === ProgrammingLanguage.Html)?.code;
      const cssCode = codes.find((c) => c.languageId === ProgrammingLanguage.Css)?.code;
      const jsCode = codes.find((c) => c.languageId === ProgrammingLanguage.Js)?.code;

      this.dataService.sourceCode$.next(htmlCode);
      this.dataService.cssCode$.next(cssCode);
      this.dataService.jsCode$.next(jsCode);

      this.dataService.codeData$.next({
        ...this.dataService.codeData$.value,
        sourceCode: htmlCode,
        cssCode,
        jsCode,
      } as any);
    } else {
      this.dataService.sourceCode$.next(codes[0].code);
      this.dataService.codeData$.next({
        sourceCode: codes[0].code,
        language: ProgrammingLanguageToCodeLanguages[codes[0].languageId],
      } as IUserCode);
    }
  };

  public onTIYWidgetInputChange = (input, lng): Observable<null> => {
    if (checkIsWeb(lng)) {
      const webLanguageMap = {
        [CodeLanguages.HTML]: this.dataService.sourceCode$,
        [CodeLanguages.CSS]: this.dataService.cssCode$,
        [CodeLanguages.JS]: this.dataService.jsCode$,
      };

      webLanguageMap[lng].next(input);

      const codes = [{
        code: this.dataService.sourceCode$.value,
        languageId: ProgrammingLanguage.Html,
      },
      {
        code: this.dataService.cssCode$.value,
        languageId: ProgrammingLanguage.Css,
      },
      {
        code: this.dataService.jsCode$.value,
        languageId: ProgrammingLanguage.Js,
      },
      ];
      const isActiveReset = JSON.stringify(codes) !== JSON.stringify(this.dataService.initialCodes.value);
      this.dataService.isTIYResetButtonActive.next(isActiveReset);
      this.dataService.expandedTIYEditorRefs.value?.forEach((e) => {
        e.current?.resetCodes(codes);
      });
    } else {
      this.dataService.sourceCode$.next(input);
      const codes = [{
        code: input,
        languageId: CodeLanguagesToProgrammingLanguage[lng],
      }];
      const isActiveReset = JSON.stringify(codes) !== JSON.stringify(this.dataService.initialCodes.value);
      this.dataService.isTIYResetButtonActive.next(isActiveReset);

      this.dataService.expandedTIYEditorRefs.value[0].current?.resetCodes(codes);
    }

    return of(null);
  };

  public onWebCodeChange = (input: string, lng: CodeLanguages): Observable<null> => {
    const webLanguageMap = {
      [CodeLanguages.HTML]: this.dataService.sourceCode$,
      [CodeLanguages.CSS]: this.dataService.cssCode$,
      [CodeLanguages.JS]: this.dataService.jsCode$,
    };

    webLanguageMap[lng].next(input);
    const codes = [{
      code: this.dataService.sourceCode$.value,
      languageId: ProgrammingLanguage.Html,
    },
    {
      code: this.dataService.cssCode$.value,
      languageId: ProgrammingLanguage.Css,
    },
    {
      code: this.dataService.jsCode$.value,
      languageId: ProgrammingLanguage.Js,
    },
    ];
    const isActiveReset = JSON.stringify(codes) !== JSON.stringify(this.dataService.initialCodes.value);
    this.dataService.isTIYResetButtonActive.next(isActiveReset);
    this.dataService.minimizedTIYEditorRefs.value?.forEach((e) => {
      e.current?.resetCodes(codes);
    });
    return of(null);
  };

  public onNotWebCodeChange = (input: string, lng: CodeLanguages): Observable<null> => {
    this.dataService.sourceCode$.next(input);
    const codes = [{
      code: input,
      languageId: CodeLanguagesToProgrammingLanguage[lng],
    }];
    const isActiveReset = JSON.stringify(codes) !== JSON.stringify(this.dataService.initialCodes.value);
    this.dataService.isTIYResetButtonActive.next(isActiveReset);
    this.dataService.minimizedTIYEditorRefs.value[0].current?.resetCodes(codes);

    return of(null);
  };

  public onMaterialNotFoundClick = (): Observable<null> => {
    const { routeData$ } = this.dataService;
    history.push(routeData$.value?.returnUrl || '/learn');
    return of(null);
  };

  public onCloseConsoleClick = (): Observable<null> => {
    this.dataService.showConsole$.next(
      !this.dataService.showConsole$.value,
    );
    return of(null);
  };

  public openConsole = (): Observable<null> => {
    this.dataService.showConsole$.next(true);
    return of(null);
  };

  public onConsoleClick = (): Observable<null> => {
    this.dataService.showConsole$.next(
      !this.dataService.showConsole$.value,
    );
    return of(null);
  };

  public onModalRunBtnClick = (input): Observable<null> => {
    const { codeData$ } = this.dataService;
    const languageType = LANGUAGE_TYPES.find(
      (item) => item.language === codeData$.value.language,
    );

    const authData = Container.take('global', AuthService).getUser();
    const materialId: number = Container.take(
      lePlaygroundContext.value,
      'materialId',
    );
    this.trackAction$.next({
      element: this.isTryItYourself
        ? 'TIY_run_lesson'
        : `codeplayground_usercode_${languageType.path}`,
      action: this.isTryItYourself ? 'click' : 'run',
      userId: authData?.id.toString(),
      entityId: this.isTryItYourself ? materialId : codeData$.value?.id,
    });

    this.modalService.close(tiyCodeInputModalId);
    this.codeService.compileCode({ input });

    return of(null);
  };

  public onHeaderCompileCode = (): Observable<ICodeCompilationResponse> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialCTAClick,
          materialCTAClickType: MaterialCTAClickType.Run,
        },
      },
    });

    this.codeService.onCompileCode();

    return of(null);
  };

  public onCloseModalSaveBtnClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialQuit,
          quitAction: MaterialQuitAction.Save,
        },
      },
    });

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialCTAClick,
          materialCTAClickType: MaterialCTAClickType.Save,
        },
      },
    });

    return of(null);
  };

  public onCloseModalDiscardBtnClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialQuit,
          quitAction: MaterialQuitAction.ModalLeave,
        },
      },
    });

    return of(null);
  };
}
